import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { View, Image, Animated, Platform, StyleSheet, StatusBar } from 'react-native';
import TouchableBounce from 'utils/packages/TouchableBounce';
import MaskedView from '@react-native-masked-view/masked-view';
import LinearGradient from 'utils/packages/linearGradient';
import { Camera, useCameraDevice } from 'utils/packages/Camera';
import { triggerHapticFeedback } from 'utils/hapticFeedback';
import Button from 'design-system/Button';
import Text from 'design-system/Text';
import Spacer from 'design-system/Spacer';
import { rem } from 'design-system/values';
import useFocusEffect from 'hooks/useFocusEffect';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { scanDocumentGradient } from '../colors';
import useCameraPermission from '../hooks/useCameraPermission';
import { useSelfieMaskView } from '../hooks/useSelfieMaskView';
import { isWeb } from '../../../constants';
const strings = {
    navTitle: 'Verify Identity',
    noAccessTitle: 'Camera Check',
    noAccess: 'Access to the camera has been prohibited.\nPlease enable it in the settings to continue.',
    noAccessButton: 'Open settings',
};
const VIEW_FINDER_HORIZONTAL_PADDING = rem(16);
const DARK_OVERLAY_NEGATIVE_MARGIN = 1;
const ScanDocumentScreen = ({ route, navigation }) => {
    useLayoutEffect(() => {
        navigation.setOptions({
            title: strings.navTitle,
        });
    }, []);
    const [focused, setFocused] = useState(false);
    const frontCamera = useCameraDevice('front');
    const [path, setPath] = useState(); // path to saved photo
    const camera = useRef(null);
    const blackOverlayOpacity = useRef(new Animated.Value(0));
    const buttonsContainerOpacity = useRef(new Animated.Value(0));
    const [accessAllowed, openSettings] = useCameraPermission();
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const { maskElement, textTop, cy } = useSelfieMaskView();
    useFocusEffect(useCallback(() => {
        setFocused(true);
        setPath(undefined);
        blackOverlayOpacity.current.setValue(0);
        return () => {
            setFocused(false);
        };
    }, []));
    const animateTo = useCallback((value, callback) => {
        if (value === 1) {
            Animated.sequence([
                Animated.timing(blackOverlayOpacity.current, {
                    toValue: 1,
                    duration: 200,
                    useNativeDriver: true,
                }),
                Animated.timing(buttonsContainerOpacity.current, {
                    toValue: 1,
                    duration: 200,
                    useNativeDriver: true,
                }),
            ]).start(callback);
        }
        else if (value === 0) {
            Animated.sequence([
                Animated.timing(buttonsContainerOpacity.current, {
                    toValue: 0,
                    duration: 200,
                    useNativeDriver: true,
                }),
                Animated.timing(blackOverlayOpacity.current, {
                    toValue: 0,
                    duration: 200,
                    useNativeDriver: true,
                }),
            ]).start(callback);
        }
    }, []);
    const onTakePhoto = useCallback(async () => {
        if (camera.current) {
            triggerHapticFeedback('clockTick');
            const photo = await camera.current.takePhoto();
            const newFile = { path: `file://${photo.path.replace('file://', '')}`, width: photo.width, height: photo.height };
            setPath(newFile);
            animateTo(1, () => {
                navigation.navigateDeprecated('WealthConfirmSelfie', {
                    selfieFile: newFile,
                    flow: route.params.flow,
                    files: route.params.files,
                    documentType: route.params.documentType,
                });
            });
        }
    }, [animateTo, route.params.documentType, route.params.files, route.params.flow]);
    const cameraView = useMemo(() => {
        if (accessAllowed === false) {
            return (<View style={[styles.noAccessContainer, { paddingTop: cy }]}>
          <Text Text-20 White centered>
            {strings.noAccessTitle}
          </Text>
          <Spacer h={8}/>
          <Text TextThin-14 White centered>
            {strings.noAccess}
          </Text>
          <Spacer h={32}/>
          <Button brand style={styles.noAccessButton} title={strings.noAccessButton} onPress={openSettings}/>
        </View>);
        }
        if (Platform.OS === 'android' && accessAllowed === undefined) {
            return <View style={styles.noAccessContainer}/>; // on android, we need to wait until we are sure that we have access to camera
        }
        if (path) {
            return <Image style={StyleSheet.absoluteFill} source={{ uri: path.path }}/>;
        }
        if (frontCamera) {
            return <Camera ref={camera} style={StyleSheet.absoluteFill} device={frontCamera} isActive={focused} photo/>;
        }
        return null;
    }, [accessAllowed, path, frontCamera, cy, openSettings, focused]);
    const blackOverlayStyle = useMemo(() => [styles.overlay, styles.dark, { opacity: blackOverlayOpacity.current }], []);
    const bottomWithOffset = useMarginBottom('bottom', rem(30), styles.controlsContainer);
    const controlsContainerStyle = useMemo(() => [
        bottomWithOffset,
        {
            opacity: blackOverlayOpacity.current.interpolate({
                inputRange: [0, 1],
                outputRange: [1, 0],
            }),
        },
    ], [bottomWithOffset]);
    const controls = useMemo(() => (<Animated.View style={controlsContainerStyle} pointerEvents={path ? 'none' : 'auto'}>
        <View style={styles.shutterContainer}>
          <TouchableBounce style={styles.shutter} onPress={onTakePhoto}/>
        </View>
      </Animated.View>), [controlsContainerStyle, path, onTakePhoto]);
    return (<View style={[styles.container]}>
      <StatusBar barStyle="dark-content" animated/>
      {cameraView}
      <View style={styles.flex} pointerEvents={accessAllowed ? 'auto' : 'none'}>
        <MaskedView style={styles.maskedView} maskElement={maskElement}>
          {accessAllowed ? (<LinearGradient style={styles.overlayContent} {...scanDocumentGradient}>
              {controls}
            </LinearGradient>) : null}
          <Animated.View style={blackOverlayStyle} pointerEvents="none"/>
        </MaskedView>
      </View>
      <View style={[styles.textsContainer, paddingHorizontalStyle, { top: textTop }]} pointerEvents="none">
        <Text Text-20 White>
          Keep your face in the oval
        </Text>
        <Spacer h={8}/>
        <Text TextThin-14 White>
          Remove glasses if necessary.
        </Text>
      </View>
    </View>);
};
const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#060606',
    },
    overlay: {
        ...StyleSheet.absoluteFillObject,
    },
    flex: {
        flex: 1,
    },
    maskedView: {
        position: 'absolute',
        top: -DARK_OVERLAY_NEGATIVE_MARGIN,
        bottom: -DARK_OVERLAY_NEGATIVE_MARGIN,
        left: -DARK_OVERLAY_NEGATIVE_MARGIN,
        right: -DARK_OVERLAY_NEGATIVE_MARGIN,
    },
    overlayContent: {
        ...Platform.select({
            ios: {
                flex: 1,
            },
            android: {
                ...StyleSheet.absoluteFillObject,
            },
        }),
    },
    dark: {
        backgroundColor: '#060606',
    },
    textsContainer: {
        position: 'absolute',
        padding: VIEW_FINDER_HORIZONTAL_PADDING,
    },
    controlsContainer: {
        position: 'absolute',
        left: 0,
        right: 0,
        alignItems: 'center',
    },
    shutterContainer: {
        width: rem(64),
        height: rem(64),
        borderRadius: rem(64),
        borderWidth: rem(4.5),
        borderColor: 'white',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#17102A',
    },
    shutter: {
        width: rem(50.6),
        height: rem(50.6),
        borderRadius: rem(50.6),
        backgroundColor: 'white',
    },
    noAccessContainer: {
        flex: 1,
        paddingHorizontal: rem(16),
        backgroundColor: '#060606',
    },
    noAccessButton: {
        alignSelf: 'center',
        paddingHorizontal: rem(60),
    },
});
export default ScanDocumentScreen;
